import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * NFL draft data: picks, league draft state, the clock, roster slots and the player pool.
 *
 * ── THIS IS A PURE MOVE ─────────────────────────────────────────────────────
 *
 * Every query, shape, dependency array and effect below was lifted VERBATIM out of
 * NFLDraftPage.tsx so both the live room and the redesigned room read the same data through
 * the same code. Nothing was "cleaned up" on the way out: the comments explaining SAB-806's
 * pagination, SAB-843's decision to stop filtering drafted players, and SAB-842's roster
 * re-read all travel with the code they describe.
 *
 * TWO THINGS THAT LOOK WRONG AND ARE NOT — do not "fix" either without measuring:
 *
 *  1. `league` is taken as the whole object, not `league.id`. The original effects depend on
 *     the league OBJECT, so a new identity from the context re-runs them even when the id is
 *     unchanged. Narrowing this to a string id would make the fetches re-run strictly less
 *     often. That is a timing change, and this extraction is not allowed one.
 *  2. RESOLVED by SAB-846, which is the ticket that also published these tables. The
 *     subscription no longer listens on `event: '*'` -- it listens on UPDATE and INSERT
 *     explicitly, because RLS is not applied to DELETE payloads and they cannot be
 *     filtered. The note below said this was worth revisiting; publishing the table is
 *     what made it matter, since until then the subscription received nothing either way.
 *
 * Sabotage cards are deliberately NOT here. `useDraftSabotageEffects` is already a
 * standalone hook that both rooms can call directly; there was nothing to extract.
 */

export interface DraftPick {
  id: string;
  round: number;
  pick_number: number;
  pick_in_round: number;
  team_id: string;
  player_id: string | null;
  player_name: string | null;
  player_position: string | null;
  player_nfl_team: string | null;
  // SAB-847: FK-joined from nfl_franchises so the draft board can show a bye without a
  // second query. All 32 franchises carry a non-null bye (5-14), verified on prod.
  player_bye_week: number | null;
  headshot_url: string | null;
  auto_picked: boolean;
  picked_by_commissioner: boolean;
}

export interface AvailablePlayer {
  id: string;
  full_name: string;
  position: string;
  adp: number | null;
  // SAB-749: roster availability (ACT / IR / PUP / NON / EXE / UFA ...). Display only —
  // never used to reorder, filter or discount. See PlayerAvailabilityBadge.
  status: string | null;
  // SAB-813: the WEEKLY INJURY REPORT, a different signal from `status` above. NULL for
  // every player until REG week 1; from then on it takes precedence on the availability
  // pill. Selected here rather than derived, because the view exposes it and the pill
  // cannot switch source at week 1 without it. See src/lib/nflAvailability.ts.
  injury_status: string | null;
  // SAB-457 (2): TRASH / ANTI projections + franchise FK for upcoming BYE join.
  proj_sab_pts: number | null;
  projected_anti_points: number | null;
  nfl_franchise_id: string | null;
  // SAB-457 (3): FK-joined bye_week_2026 from nfl_franchises (cached at
  // migration time from nfl_games; fail-loud derivation guarantees all
  // 32 franchises have a non-null value in 5-14).
  // SAB-457 (7): alias surfaces in the search filter (e.g. "BAL", "DAL").
  franchise: { bye_week_2026: number | null; alias: string | null } | null;
  headshot_url: string | null;
}

/**
 * Minimal shape this hook needs. Only `id` is read — but callers must pass the CONTEXT'S
 * LEAGUE OBJECT ITSELF, not `{ id }`, because the effects below depend on this reference's
 * identity. Reconstructing an object per render would re-run every fetch on every render;
 * narrowing to a string id would re-run them less often than today. See note 1 above.
 *
 * No index signature: an interface like NFLLeague has no implicit one, so adding it here
 * would reject the very object this is meant to receive.
 */
export interface DraftDataLeague {
  id: string;
}


/**
 * ── WHY THIS IS TWO HOOKS AND NOT ONE ───────────────────────────────────────
 *
 * There is a genuine cycle in the original component:
 *
 *   useDraftSabotageEffects needs `draftStatus`
 *   ...and returns `refetchActive`, which the picks subscription depends on
 *   ...and `refetchActive` is useCallback(..., [leagueId, draftStatus])
 *
 * So its identity changes whenever draftStatus changes, which means the original
 * subscription effect TEARS DOWN AND RE-SUBSCRIBES on every draftStatus transition. That
 * is the shipped behaviour, wasteful or not.
 *
 * Collapsing this into one hook would force `refetchActive` behind a ref to break the
 * cycle, which would drop it from the dependency array and stop those re-subscriptions --
 * a behaviour change smuggled into a refactor. Splitting instead preserves the component's
 * original hook order exactly:
 *
 *   const state = useNFLDraftState();
 *   const { refetchActive } = useDraftSabotageEffects({ draftStatus: state.draftStatus });
 *   useNFLDraftSync({ league, state, refetchActive });
 */
export function useNFLDraftState() {
  const [picks, setPicks] = useState<DraftPick[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);
  const [pauseReason, setPauseReason] = useState<string | null>(null);
  const [pausedRemaining, setPausedRemaining] = useState<number | null>(null);
  /**
   * settings.draft_paused_at, tracked separately from draft_status.
   *
   * Two pause signals that CAN disagree. nfl_pause_resume_lock writes both, but
   * nfl-auto-pick defends against "paused_at set but a deadline present" as a reachable
   * inconsistent state (SAB-534 Chunk 2) -- and paused_at is the one the SERVER obeys:
   * any league carrying it is SKIPPED and never advances. Landed on main in #288; the
   * extraction must carry it, or the live room silently loses the fix.
   */
  const [draftPausedAt, setDraftPausedAt] = useState<string | null>(null);
  const [currentPickNumber, setCurrentPickNumber] = useState(1);
  const [totalPicks, setTotalPicks] = useState(0);
  const [pickDeadline, setPickDeadline] = useState<string | null>(null);
  const [onTheClockTeamIdRaw, setOnTheClockTeamId] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [slotByPlayerId, setSlotByPlayerId] = useState<Record<string, string>>({});
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);

  /**
   * The team on the clock, or null when no draft is running.
   *
   * fetchPicks derives the raw value from "first pick row with no player", which is
   * meaningful ONLY while a draft is live. It is wrong in three reachable states:
   *   pending    pick rows exist before the draft starts
   *   cancelled  nfl-cancel-draft CLEARS the player ids but KEEPS the 152 rows, so the
   *              first unfilled row is pick 1 again
   *   completed  no unfilled rows, but a stale value can persist
   *
   * Gated here rather than at each render because the raw value is simply not a fact
   * outside a live draft: the live room only avoids the symptom by not rendering those
   * statuses, and the redesigned room renders all of them. Observed on staging after a
   * cancel: "PENDING | EBot 07 is on the clock | 0 picks made".
   */
  const onTheClockTeamId =
    draftStatus === 'in_progress' || draftStatus === 'paused' ? onTheClockTeamIdRaw : null;

  return {
    picks, setPicks,
    isLoading, setIsLoading,
    draftStatus, setDraftStatus,
    pauseReason, setPauseReason,
    pausedRemaining, setPausedRemaining,
    draftPausedAt, setDraftPausedAt,
    currentPickNumber, setCurrentPickNumber,
    totalPicks, setTotalPicks,
    pickDeadline, setPickDeadline,
    onTheClockTeamId, setOnTheClockTeamId,
    timeLeft, setTimeLeft,
    slotByPlayerId, setSlotByPlayerId,
    availablePlayers, setAvailablePlayers,
    playersLoading, setPlayersLoading,
  };
}

export type NFLDraftState = ReturnType<typeof useNFLDraftState>;

export interface UseNFLDraftSyncArgs {
  league: DraftDataLeague | null;
  state: NFLDraftState;
  refetchActive: () => void;
  /**
   * SAB-992: re-read the league's teams after an nfl_teams UPDATE arrives. Supplied by the
   * room from NFLLeagueContext, which owns the ['nfl-teams', id] query.
   *
   * Optional because every draft-room test harness mocks that context; an absent callback
   * simply means no refetch, which is the pre-SAB-992 behaviour rather than a crash.
   */
  refetchTeams?: () => void;
}

/** The fetchers and effects, lifted verbatim. Call AFTER useDraftSabotageEffects. */
export function useNFLDraftSync({ league, state, refetchActive, refetchTeams }: UseNFLDraftSyncArgs) {
  const {
    setPicks, setIsLoading, setDraftStatus, setPauseReason, setPausedRemaining,
    setCurrentPickNumber, setTotalPicks, setPickDeadline, setOnTheClockTeamId, setDraftPausedAt,
    setTimeLeft, setSlotByPlayerId, setAvailablePlayers, setPlayersLoading,
    draftStatus, currentPickNumber, pickDeadline, draftPausedAt,
  } = state;

  // Fetch league draft state
  const fetchDraftState = useCallback(async () => {
    if (!league) return;
    const { data } = await (supabase as any)
      .from('nfl_leagues')
      .select('draft_status, settings')
      .eq('id', league.id)
      .single();
    if (!data) return;
    setDraftStatus(data.draft_status);
    const settings = data.settings || {};
    setCurrentPickNumber(settings.draft_current_pick || 1);
    setTotalPicks(settings.draft_total_picks || 0);
    setPickDeadline(settings.draft_pick_deadline || null);
    // SAB-534 Chunk 2: unified pause state
    setPauseReason(settings.draft_pause_reason || null);
    setDraftPausedAt(settings.draft_paused_at || null);
    setPausedRemaining(
      typeof settings.draft_paused_remaining_seconds === 'number'
        ? settings.draft_paused_remaining_seconds
        : null,
    );
  }, [league, setDraftStatus, setCurrentPickNumber, setTotalPicks, setPickDeadline, setPauseReason, setPausedRemaining, setDraftPausedAt]);

  // SAB-842: the authoritative slot each drafted player occupies. Written by
  // nfl_acquire_pick_lock at pick time against nfl_slot_eligibility, so this is the
  // roster the rest of the product already agrees with -- never re-derived here.
  const fetchRosterSlots = useCallback(async () => {
    if (!league) return;
    const { data, error } = await (supabase as any)
      .from('nfl_rosters')
      .select('player_id, position_slot')
      .eq('league_id', league.id)
      .eq('is_active', true);
    if (error) { console.error('[roster-slots] load failed:', error.message); return; }
    const map: Record<string, string> = {};
    for (const r of data || []) {
      if (r.player_id && r.position_slot) map[r.player_id] = r.position_slot;
    }
    setSlotByPlayerId(map);
  }, [league, setSlotByPlayerId]);

  // Fetch draft picks
  const fetchPicks = useCallback(async () => {
    if (!league) return;
    const { data, error } = await (supabase as any)
      .from('nfl_draft_picks')
      .select(`
        id, round, pick_number, pick_in_round, team_id, player_id, auto_picked, picked_by_commissioner,
        nfl_players (
          full_name, position, nfl_franchise_id, headshot_url,
          franchise:nfl_franchises ( alias, bye_week_2026 )
        )
      `)
      .eq('league_id', league.id)
      .order('pick_number');
    if (error) { console.error('Failed to fetch draft picks:', error); return; }

    const all: DraftPick[] = (data || []).map((p: any) => ({
      id: p.id,
      round: p.round,
      pick_number: p.pick_number,
      pick_in_round: p.pick_in_round,
      team_id: p.team_id,
      player_id: p.player_id,
      player_name: p.nfl_players?.full_name || null,
      player_position: p.nfl_players?.position || null,
      player_nfl_team: p.nfl_players?.franchise?.alias || null,
      player_bye_week: p.nfl_players?.franchise?.bye_week_2026 ?? null,
      headshot_url: p.nfl_players?.headshot_url || null,
      auto_picked: p.auto_picked || false,
      picked_by_commissioner: p.picked_by_commissioner || false,
    }));
    setPicks(all);

    // Find who is on the clock
    const nextEmpty = all.find(p => !p.player_id);
    if (nextEmpty) setOnTheClockTeamId(nextEmpty.team_id);
    else setOnTheClockTeamId(null);
  }, [league, setPicks, setOnTheClockTeamId]);

  // Fetch available players for picking
  const fetchAvailablePlayers = useCallback(async () => {
    if (!league) return;
    setPlayersLoading(true);

    // SAB-457 (2 + 3): pull projection columns + franchise FK + embedded
    // bye_week_2026 alongside the existing 5 fields. PostgREST embedded select uses
    // the FK nfl_players.nfl_franchise_id -> nfl_franchises.id
    // (constraint nfl_players_nfl_franchise_id_fkey). Server-side sort
    // stays on adp for now — TRASH-desc default + click-to-sort lands
    // in commits 4 + 5.
    //
    // ── THIS READS v_draftable_players, NOT nfl_players ──────────────────────
    //
    // SAB-996: the previous version of this comment said the opposite -- "Reads
    // nfl_players directly (per ticket lock; does not depend on SAB-443's
    // v_draftable_players view landing)". SAB-443's view DID land and the query moved to
    // it; only the comment stayed behind. That cost real time: instrumenting a focus
    // measurement against `nfl_players` counted ZERO pool reads and very nearly produced
    // the confident wrong answer that focus does not refetch the pool. A comment claiming
    // the wrong table is worse than no comment, because it is the thing the next reader
    // trusts instead of checking.
    //
    // What the view is, as of today (SAB-1033 changed this): nfl_players minus the seven
    // positions with no nfl_slot_eligibility mapping (OL, G, T, OT, C, OG, LS). It no
    // longer filters on active_2026.
    //
    // That gate was removed because it put a RELEVANCE signal in the EXISTENCE layer: a
    // player the roster sync could not find on an NFL roster vanished from the pool
    // entirely, unmarked -- including players someone already owned. The flag is still
    // SELECTED and the row is marked instead. Pool went 2086 -> 2795 on staging.
    //
    // The seven excluded positions stay excluded: that is a genuine existence rule (they
    // are unrosterable in every league -- the 420 SAB-995 independently found), not a
    // relevance one. The draft room has therefore always excluded them; it
    // was the Players page, reading nfl_players through the leaderboard RPC, that showed
    // them with an +Add button until SAB-995 filtered that path too.
    // SAB-806: PAGINATE. PostgREST enforces a server-side max-rows of 1000, so a
    // client .limit() above that is SILENTLY CLAMPED -- the request asks for 3000 and
    // gets exactly 1000 back with no error. Because board-position players sort first
    // under nullsFirst:false (IDP carry a NULL adp for year one), those 1000 rows were
    // ALL board positions and the IDP tab rendered "No players found" on the live site.
    //
    // Raising the client limit could not fix this and did not: verified in a browser
    // against prod with the raised limit deployed. Every service-role SQL check passed
    // because max-rows does not apply to them, which is precisely why this needed a
    // click rather than a query.
    const PAGE = 1000;
    const rows: any[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data: page, error } = await (supabase as any)
        .from('v_draftable_players')
        .select('id, full_name, position, status, injury_status, adp, proj_sab_pts, projected_anti_points, nfl_franchise_id, headshot_url, franchise:nfl_franchises ( bye_week_2026, alias )')
        .order('adp', { ascending: true, nullsFirst: false })
        .order('id', { ascending: true })
        .range(from, from + PAGE - 1);
      if (error || !page || page.length === 0) break;
      rows.push(...page);
      if (page.length < PAGE) break;
      if (rows.length >= 5000) break; // hard stop; pool is ~2,400
    }
    const data = rows;

    // SAB-843: drafted players are NO LONGER removed here.
    //
    // Dropping them made a player you were hunting vanish mid-search with nothing said.
    // From the manager's side that is indistinguishable from a broken filter -- there is
    // no way to tell "someone took him" from "the search is wrong", at the exact moment
    // it matters. They stay in the list and are marked instead, the same treatment
    // SAB-749 already gives unavailable players ("the row is marked, never removed or
    // reordered").
    //
    // This also drops `picks` from the dependency list, which was only ever here to build
    // draftedIds. It was re-running this entire paginated ~2,400-row fetch on EVERY pick
    // in the league. Drafted state now comes from a memo over data already in hand.
    setAvailablePlayers((data || []) as AvailablePlayer[]);
    setPlayersLoading(false);
  }, [league, setAvailablePlayers, setPlayersLoading]);

  // Initial load
  useEffect(() => {
    if (!league) { setIsLoading(false); return; }
    setIsLoading(true);
    Promise.all([fetchDraftState(), fetchPicks(), fetchRosterSlots()])
      .then(() => setIsLoading(false));
  }, [league, fetchDraftState, fetchPicks, fetchRosterSlots, setIsLoading]);

  // SAB-842: re-read the roster whenever the pick pointer moves -- that is exactly when a
  // new slot was just filled.
  //
  // 'completed' is included deliberately. Without it the FINAL picks' slot assignments are
  // never fetched: the draft flips to completed, this effect stops firing, and the roster
  // view keeps rendering whatever the last in-progress fetch returned. Observed on staging
  // -- the database held 19 picks and 19 correctly-assigned roster rows while the rail
  // showed 17, with no error anywhere, and a page reload corrected it.
  //
  // That is precisely the SAB-842 failure class: the server's assignment is authoritative
  // and the view silently disagreed with it.
  useEffect(() => {
    if (draftStatus === 'in_progress' || draftStatus === 'completed') fetchRosterSlots();
  }, [currentPickNumber, draftStatus, fetchRosterSlots]);

  // Load the player pool.
  //
  // SAB-880: `pending` as well as `in_progress`. Gating on in_progress ALONE meant the V2
  // room could never show players in any state it serves, because the two conditions are
  // mutually exclusive: rule (e) routes in_progress and paused to the LEGACY room, so V2
  // only ever sees pending or completed, and neither fetched. `availablePlayers` was
  // permanently []. That emptied the PLAYERS panel and, less obviously, broke queue NAMES --
  // NFLDraftRoomV2 resolves queued ids through this array, so every row fell back to
  // "Queued player · <8 chars>" on data that was perfectly valid.
  //
  // Pre-draft is also the state where the pool matters most: queueing players before the
  // draft starts is the whole point of a queue.
  //
  // `completed` stays excluded, deliberately (#293) -- the pool is not fetched for a
  // finished draft and "0 players" would be a false statement about it rather than a small
  // number. The centre toolbar drops the player count for completed on the same reasoning.
  //
  // This hook is shared with the LEGACY room (NFLDraftPage uses the same
  // useNFLDraftState/useNFLDraftSync). Legacy never serves `pending` -- rule (e) sends it
  // here -- so this widens the gate without changing anything legacy actually does.
  useEffect(() => {
    if (draftStatus === 'in_progress' || draftStatus === 'pending') fetchAvailablePlayers();
  }, [draftStatus, fetchAvailablePlayers]);

  // Countdown timer
  useEffect(() => {
    // draftPausedAt included (#288): a paused league can still carry a stale deadline,
    // and counting it down is what produced "0:00, hurry" on a stopped draft.
    if (!pickDeadline || draftStatus !== 'in_progress' || draftPausedAt) { setTimeLeft(null); return; }
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(pickDeadline).getTime() - Date.now()) / 1000));
      setTimeLeft(diff);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [pickDeadline, draftStatus, draftPausedAt, setTimeLeft]);

  // Real-time subscription for draft picks
  useEffect(() => {
    if (!league) return;
    const channel = supabase
      .channel(`nfl-draft-${league.id}`)
      // SAB-846: explicit events, never '*'. Supabase does not apply RLS to DELETE events
      // and they cannot be filtered, so '*' would hand every subscriber every deletion on
      // nfl_draft_picks regardless of league. The handler only refetches and reads nothing
      // from the payload, so it has no use for DELETE -- and undo-pick does delete rows.
      // Same rule useDraftChat already applies.
      //
      // UPDATE IS THE LOAD-BEARING ONE, and INSERT alone would have been this ticket's own
      // bug reintroduced. The board pre-creates every pick row when the draft is built and
      // a pick FILLS it: nfl_acquire_pick_lock does
      //   UPDATE nfl_draft_picks SET player_id = ..., picked_at = ... WHERE id = ...
      // Prod bears this out -- 1,450 pick rows, 0 with a null player_id. An INSERT-only
      // subscription would receive nothing at all.
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'nfl_draft_picks',
        filter: `league_id=eq.${league.id}`,
      }, () => {
        fetchPicks();
        fetchDraftState();
        // Active sabotage effects expire on pick boundaries (pick-scoped) and
        // round boundaries (round-scoped). Refetch so the UI sheds them.
        refetchActive();
      })
      // INSERT as well, for the row-creation path when a draft board is built or extended.
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'nfl_draft_picks',
        filter: `league_id=eq.${league.id}`,
      }, () => {
        fetchPicks();
        fetchDraftState();
        refetchActive();
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'nfl_leagues',
        filter: `id=eq.${league.id}`,
      }, () => {
        fetchDraftState();
      })
      // SAB-992: auto_draft_enabled changes were invisible to everyone but the toggling
      // manager, because nothing in the room watched nfl_teams at all.
      //
      // UPDATE only, and filtered by league, for the SAB-846 reason above: '*' would also
      // deliver DELETEs, which Supabase does not RLS-filter and cannot filter by league.
      //
      // The handler refetches rather than reading the payload. A realtime row arrives
      // RLS-filtered per subscriber and carries only the changed row, so trusting it would
      // mean maintaining a second, partial copy of team state beside the context's. One
      // refetch through the existing query key keeps exactly one source of truth -- and it
      // means a commissioner flipping the column straight in SQL propagates identically,
      // because the subscription is on the TABLE, not on any client's own write.
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'nfl_teams',
        filter: `league_id=eq.${league.id}`,
      }, () => {
        refetchTeams?.();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [league, fetchPicks, fetchDraftState, refetchActive, refetchTeams]);

  return { fetchDraftState, fetchPicks, fetchRosterSlots, fetchAvailablePlayers };
}
